/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/quote-management/components/QuoteCommentsList/QuoteCommentsList.css';

export interface QuoteCommentsListProps
  extends HTMLAttributes<HTMLUListElement> {
  comments: Array<{
    uid: string;
    createdAt: VNode;
    author: VNode;
    text: VNode;
    attachments?: Array<{
      name: string;
      url: string;
    }>;
  }>;
}

export const QuoteCommentsList: FunctionComponent<QuoteCommentsListProps> = ({
  className,
  comments,
  ...props
}) => {
  const dictionary = useText({
    emptyState: 'NegotiableQuote.Manage.quoteComments.emptyState',
    by: 'NegotiableQuote.Manage.quoteComments.by',
    attachments: 'NegotiableQuote.Manage.quoteComments.attachments',
  });

  if (!comments || comments.length === 0) {
    return (
      <ul
        {...props}
        className={classes([
          'quote-management-quote-comments-list',
          'quote-management-quote-comments-list--empty',
          className,
        ])}
        data-testid="quote-comments-list"
      >
        <li
          className="quote-management-quote-comments-list__empty-state"
          data-testid="quote-comments-empty-state"
        >
          {dictionary.emptyState}
        </li>
      </ul>
    );
  }

  return (
    <ul
      {...props}
      className={classes(['quote-management-quote-comments-list', className])}
      data-testid="quote-comments-list"
    >
      {comments.map((comment) => (
        <li
          key={comment.uid}
          className="quote-management-quote-comments-list__item"
          data-testid={`quote-comment-${comment.uid}`}
        >
          <div className="quote-management-quote-comments-list__header">
            <VComponent
              className="quote-management-quote-comments-list__date"
              node={comment.createdAt}
              data-testid={`quote-comment-date-${comment.uid}`}
            />
            <span className="quote-management-quote-comments-list__by">
              {dictionary.by}
            </span>
            <VComponent
              className="quote-management-quote-comments-list__author"
              node={comment.author}
              data-testid={`quote-comment-author-${comment.uid}`}
            />
          </div>
          <VComponent
            className="quote-management-quote-comments-list__text"
            node={comment.text}
            data-testid={`quote-comment-text-${comment.uid}`}
          />
          {comment.attachments && comment.attachments.length > 0 && (
            <div
              className="quote-management-quote-comments-list__attachments"
              data-testid={`quote-comment-attachments-${comment.uid}`}
            >
              <span className="quote-management-quote-comments-list__attachments-label">
                {dictionary.attachments}
              </span>
              {comment.attachments.map((attachment, index) => (
                <a
                  key={`${comment.uid}-attachment-${index}`}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="quote-management-quote-comments-list__attachment-link"
                  data-testid={`quote-comment-attachment-${comment.uid}-${index}`}
                >
                  {attachment.name}
                </a>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};
